import React, { useState, useEffect, useMemo } from 'react';
import { CareerLevel, ConsultantNode, PayoutBreakdown } from '../../types/career';
import { Drawer } from '../ui/Drawer';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Slider } from '../ui/Slider';
import { getLevelTitle, getLevelDefinition } from '../../math/careerRules';
import { evaluateCareerLevel } from '../../math/qualification';
import { MetricsGrid } from '../dashboard/MetricsGrid';
import { calculateNodePayout } from '../../math/payout';
import { useSimulationStore } from '../../store/useSimulationStore';
import { findNodeById } from '../../utils/treeUtils';
import {
  User,
  UserPlus,
  Trash2,
  Power,
  Edit3,
  Check,
} from 'lucide-react';

export interface NodeEditorDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  selectedNode: ConsultantNode | null;
  onUpdateNode: (id: string, data: Partial<ConsultantNode>) => void;
  onAddChild?: (parentId: string) => void;
  onDeleteNode?: (id: string) => void;
  onToggleActive?: (id: string) => void;
  isReadOnly?: boolean;
  payout?: PayoutBreakdown;
  previousMonthGrossIncome?: number;
}

interface NodeEditorDrawerContentProps extends Omit<NodeEditorDrawerProps, 'selectedNode'> {
  selectedNode: ConsultantNode;
}

const NodeEditorDrawerContent: React.FC<NodeEditorDrawerContentProps> = ({
  isOpen,
  onClose,
  selectedNode,
  onUpdateNode,
  onAddChild,
  onDeleteNode,
  onToggleActive,
  isReadOnly = false,
  payout: propPayout,
  previousMonthGrossIncome: propPreviousMonthGrossIncome,
}) => {

  const [name, setName] = useState(selectedNode.name);
  const [level, setLevel] = useState<CareerLevel>(selectedNode.level);
  const [isActive, setIsActive] = useState(selectedNode.isActive);
  const [personalMonthlyAE, setPersonalMonthlyAE] = useState(selectedNode.personalMonthlyAE);
  const [personalCumulativeAE, setPersonalCumulativeAE] = useState(selectedNode.personalCumulativeAE);
  const [teamCumulativeAE, setTeamCumulativeAE] = useState(
    selectedNode.teamCumulativeAE !== undefined
      ? selectedNode.teamCumulativeAE
      : Math.max(0, selectedNode.totalCumulativeAE - selectedNode.personalCumulativeAE)
  );
  const [monthlyMeetings, setMonthlyMeetings] = useState(selectedNode.monthlyMeetings);
  const [conversionRate, setConversionRate] = useState(selectedNode.conversionRate);
  const [recruitmentRate, setRecruitmentRate] = useState(selectedNode.recruitmentRate);

  const storeRateCoeff = useSimulationStore((state) => state.commissionRateCoefficient);
  const usdExchangeRate = useSimulationStore((state) => state.usdExchangeRate);
  const mode = useSimulationStore((state) => state.mode);
  const selectedMonth = useSimulationStore((state) => state.selectedMonth);
  const simulationResult = useSimulationStore((state) => state.simulationResult);

  // Sync state when selectedNode changes
  useEffect(() => {
    setName(selectedNode.name);
    setLevel(selectedNode.level);
    setIsActive(selectedNode.isActive);
    setPersonalMonthlyAE(selectedNode.personalMonthlyAE);
    setPersonalCumulativeAE(selectedNode.personalCumulativeAE);
    setTeamCumulativeAE(
      selectedNode.teamCumulativeAE !== undefined
        ? selectedNode.teamCumulativeAE
        : Math.max(0, selectedNode.totalCumulativeAE - selectedNode.personalCumulativeAE)
    );
    setMonthlyMeetings(selectedNode.monthlyMeetings);
    setConversionRate(selectedNode.conversionRate);
    setRecruitmentRate(selectedNode.recruitmentRate);
  }, [selectedNode]);

  // Monthly turnover is summed from downline consultants to the leader
  const childrenMonthly = useMemo(() => {
    return selectedNode.children.reduce((sum, c) => sum + (c.totalMonthlyAE || 0), 0);
  }, [selectedNode.children]);

  // Direct branch levels from children
  const directBranchLevels = useMemo(() => {
    return selectedNode.children.map((c) => c.level);
  }, [selectedNode.children]);

  // Total cumulative units
  const currentTotalCumulativeAE = personalCumulativeAE + teamCumulativeAE;

  // Evaluate qualified level based on cumulative units and direct branches
  const qualifiedLevel = useMemo(() => {
    return evaluateCareerLevel(
      level,
      currentTotalCumulativeAE,
      personalCumulativeAE,
      directBranchLevels
    );
  }, [level, currentTotalCumulativeAE, personalCumulativeAE, directBranchLevels]);

  // Keep state level synchronized if units change such that current level is no longer valid
  useEffect(() => {
    if (level !== qualifiedLevel) {
      setLevel(qualifiedLevel);
    }
  }, [qualifiedLevel, level]);

  const handleSelectLevel = (targetLvl: CareerLevel) => {
    const targetDef = getLevelDefinition(targetLvl);
    const minCum = targetDef.minCumulativeUnits;
    const minPersonal = targetDef.minPersonalUnits ?? 0;

    let nextPersonal = personalCumulativeAE;
    let nextTeam = teamCumulativeAE;

    if (nextPersonal < minPersonal) {
      nextPersonal = minPersonal;
      setPersonalCumulativeAE(nextPersonal);
    }
    if (nextPersonal + nextTeam < minCum) {
      nextTeam = Math.max(0, minCum - nextPersonal);
      setTeamCumulativeAE(nextTeam);
    }

    const nextEvaluated = evaluateCareerLevel(
      targetLvl,
      nextPersonal + nextTeam,
      nextPersonal,
      directBranchLevels
    );
    setLevel(nextEvaluated);
  };

  // Compute activeNode reflecting current inputs/sliders
  const activeNode: ConsultantNode = useMemo(() => {
    const effectivePersonalMonthlyAE = isActive ? personalMonthlyAE : 0;
    const effectiveTotalMonthlyAE = effectivePersonalMonthlyAE + childrenMonthly;
    const effectiveTotalCumulativeAE = personalCumulativeAE + teamCumulativeAE;

    return {
      ...selectedNode,
      name,
      level,
      isActive,
      personalMonthlyAE: effectivePersonalMonthlyAE,
      personalCumulativeAE,
      teamMonthlyAE: childrenMonthly,
      teamCumulativeAE,
      totalMonthlyAE: effectiveTotalMonthlyAE,
      totalCumulativeAE: effectiveTotalCumulativeAE,
      monthlyMeetings,
      conversionRate,
      recruitmentRate,
    };
  }, [
    selectedNode,
    name,
    level,
    isActive,
    personalMonthlyAE,
    personalCumulativeAE,
    teamCumulativeAE,
    childrenMonthly,
    monthlyMeetings,
    conversionRate,
    recruitmentRate,
  ]);

  const calculatedPayout = useMemo(() => {
    return calculateNodePayout(activeNode, usdExchangeRate, storeRateCoeff);
  }, [activeNode, usdExchangeRate, storeRateCoeff]);

  const finalPayout = useMemo(() => {
    const isModified =
      activeNode.level !== selectedNode.level ||
      activeNode.isActive !== selectedNode.isActive ||
      activeNode.personalMonthlyAE !== (selectedNode.isActive ? selectedNode.personalMonthlyAE : 0) ||
      activeNode.personalCumulativeAE !== selectedNode.personalCumulativeAE ||
      activeNode.teamCumulativeAE !== selectedNode.teamCumulativeAE;

    if (propPayout && !isModified) {
      return propPayout;
    }
    return calculatedPayout;
  }, [propPayout, calculatedPayout, activeNode, selectedNode]);

  // Previous month gross income calculation for trend indicator
  const prevStep =
    mode === 'pathway' && selectedMonth > 1 && simulationResult?.steps[selectedMonth - 2]
      ? simulationResult.steps[selectedMonth - 2]
      : null;
  const prevSelectedNode = prevStep ? findNodeById(prevStep.rootConsultant, selectedNode.id) : null;
  const calculatedPrevGross = prevSelectedNode
    ? calculateNodePayout(prevSelectedNode, usdExchangeRate, storeRateCoeff).totalGrossIncomeUAH
    : undefined;
  const finalPrevGross =
    propPreviousMonthGrossIncome !== undefined
      ? propPreviousMonthGrossIncome
      : calculatedPrevGross;

  const handleToggleActive = () => {
    const nextActive = !isActive;
    setIsActive(nextActive);
    onToggleActive?.(selectedNode.id);
  };

  const handleSave = () => {
    if (isReadOnly) {
      onClose();
      return;
    }

    onUpdateNode(selectedNode.id, {
      name,
      level,
      isActive,
      personalMonthlyAE,
      personalCumulativeAE,
      teamCumulativeAE,
      teamMonthlyAE: childrenMonthly,
      totalMonthlyAE: (isActive ? personalMonthlyAE : 0) + childrenMonthly,
      totalCumulativeAE: personalCumulativeAE + teamCumulativeAE,
      monthlyMeetings,
      conversionRate,
      recruitmentRate,
    });
    onClose();
  };

  const isRoot = selectedNode.parentId === null;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      width="max-w-lg"
      title={
        <div className="flex items-center gap-2">
          <Edit3 className="w-4 h-4 text-accent-emerald" />
          <span>{isReadOnly ? 'Інформація про консультанта' : 'Редагування консультанта'}</span>
        </div>
      }
      subtitle={`ID: ${selectedNode.id}`}
      footer={
        <div className="flex items-center justify-between w-full">
          {!isReadOnly && !isRoot && onDeleteNode && (
            <Button
              variant="danger"
              size="sm"
              leftIcon={<Trash2 className="w-4 h-4" />}
              onClick={() => {
                onDeleteNode(selectedNode.id);
                onClose();
              }}
            >
              Видалити
            </Button>
          )}

          <div className="flex items-center gap-2 ml-auto">
            <Button variant="ghost" size="sm" onClick={onClose}>
              Скасувати
            </Button>
            {!isReadOnly && (
              <Button
                variant="primary"
                size="sm"
                leftIcon={<Check className="w-4 h-4" />}
                onClick={handleSave}
              >
                Зберегти
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div data-tour="node-editor-drawer" className="space-y-6">
        {/* Profile Card Summary */}
        <div className="glass-card rounded-xl p-4 border border-white/5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-surface-hover border border-white/10 flex items-center justify-center text-accent-emerald">
                <User className="w-6 h-6" />
              </div>
              <div>
                <h4 className="font-bold text-base text-text-primary">{name}</h4>
                <div className="flex items-center gap-2 mt-0.5">
                  <Badge level={level} size="sm">
                    Р{level} • {getLevelTitle(level)}
                  </Badge>
                  {isRoot && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-300 border border-cyan-500/20 font-medium">
                      Лідер структури (Root)
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Active Toggle */}
            {onToggleActive && !isReadOnly && (
              <button
                type="button"
                onClick={handleToggleActive}
                className={`
                  p-2 rounded-xl border transition-all cursor-pointer flex items-center gap-1.5 text-xs font-semibold
                  ${
                    isActive
                      ? 'bg-emerald-500/15 text-accent-neon border-emerald-500/30 hover:bg-emerald-500/25'
                      : 'bg-slate-500/20 text-slate-300 border-slate-500/30 hover:bg-slate-500/30'
                  }
                `}
                title={isActive ? 'Деактивувати консультанта' : 'Активувати консультанта'}
              >
                <Power className="w-4 h-4" />
                <span>{isActive ? 'Активний' : 'Неактивний'}</span>
              </button>
            )}
          </div>

          {/* 6 Key Performance Indicators (MetricsGrid) */}
          <div data-tour="node-kpi-metrics" className="pt-2 border-t border-white/5 space-y-2">
            <div className="text-[11px] font-semibold text-text-secondary uppercase tracking-wider">
              Показники консультанта:
            </div>
            <MetricsGrid
              rootNode={activeNode}
              payout={finalPayout}
              previousMonthGrossIncome={finalPrevGross}
            />
          </div>
        </div>

        {/* Read-Only Notice in Pathway Mode */}
        {isReadOnly && (
          <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-3 text-xs text-cyan-300">
            У режимі «Шлях новачка» показники розраховуються математичною моделлю симуляції помісячно. Для вільного редагування вузлів перемкніться в «Конструктор структури».
          </div>
        )}

        {/* Editable Fields (Sandbox Mode) */}
        {!isReadOnly && (
          <div className="space-y-4">
            {/* Name Input */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-text-primary">
                Ім'я консультанта / назва гілки:
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="
                  w-full px-3 py-2 text-base sm:text-sm rounded-xl bg-slate-900/90 text-text-primary
                  border border-white/10 focus:border-accent-emerald focus:outline-none font-medium
                "
              />
            </div>

            {/* Career Level Selector */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs font-semibold text-text-primary">
                <span>Кар'єрний рівень (1–8):</span>
                <span className="text-[11px] font-normal text-text-muted">
                  {level === 1 && 'Р1: від 0 Е'}
                  {level === 2 && 'Р2: від 100 накопичених Е'}
                  {level === 3 && 'Р3: від 700 заг. Е (від 300 особ.)'}
                  {level === 4 && 'Р4: від 3 000 заг. Е (від 300 особ.) + [2, 2]'}
                  {level === 5 && 'Р5: від 10 000 заг. Е (від 300 особ.) + [3, 3, 2]'}
                  {level === 6 && 'Р6: від 25 000 заг. Е (від 300 особ.) + [4, 4, 3, 2]'}
                  {level === 7 && 'Р7: від 60 000 заг. Е (від 300 особ.) + [5, 5, 4, 3]'}
                  {level === 8 && 'Р8: від 100 000 заг. Е (від 300 особ.) + [6, 6, 5, 4]'}
                </span>
              </div>
              <div className="grid grid-cols-4 gap-1.5">
                {([1, 2, 3, 4, 5, 6, 7, 8] as CareerLevel[]).map((lvl) => (
                  <button
                    key={lvl}
                    type="button"
                    onClick={() => handleSelectLevel(lvl)}
                    className={`
                      py-2 px-2 rounded-xl text-xs font-mono font-bold border transition-all cursor-pointer
                      ${
                        level === lvl
                          ? 'bg-accent-emerald text-slate-950 border-accent-emerald shadow-[0_0_10px_rgba(16,185,129,0.4)]'
                          : 'bg-surface-hover text-text-secondary hover:text-text-primary border-white/5'
                      }
                    `}
                  >
                    Р{lvl}
                  </button>
                ))}
              </div>
              {level < 3 && personalCumulativeAE >= 300 && currentTotalCumulativeAE < 700 && (
                <div className="text-[11px] text-amber-400/90 bg-amber-500/10 border border-amber-500/20 rounded-lg p-2 leading-tight">
                  Особисті бали ({personalCumulativeAE} Е) відповідають вимозі Р3, але для закриття Р3 потрібно мінімум 700 загальних накопичених Е (зараз {currentTotalCumulativeAE} Е, бракує {700 - currentTotalCumulativeAE} Е).
                </div>
              )}
            </div>

            {/* Units Editing: Monthly (Personal input + Auto-summed Team) and Cumulative (Personal + Team) */}
            <div className="space-y-4 pt-1">
              {/* 1. Monthly Units Section (Personal + Auto-summed Team from branches) */}
              <div className="p-3.5 rounded-xl bg-slate-900/60 border border-white/10 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-text-primary uppercase tracking-wide">
                    Місячний обіг (Е)
                  </span>
                  <span className="font-mono text-xs text-accent-neon font-bold">
                    Всього: {(isActive ? personalMonthlyAE : 0) + childrenMonthly} Е
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Personal Monthly AE */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs">
                      <label htmlFor="drawer-personal-monthly-ae" className="font-semibold text-text-primary">
                        Особистий обіг (Е):
                      </label>
                      <span className="font-mono text-[11px] text-emerald-400 font-semibold">
                        {personalMonthlyAE} Е
                      </span>
                    </div>
                    <p className="text-[10px] text-text-muted leading-tight">
                      Власні продажі за місяць
                    </p>
                    <div className="relative">
                      <input
                        id="drawer-personal-monthly-ae"
                        type="number"
                        min={0}
                        step={1}
                        value={personalMonthlyAE === 0 ? '' : personalMonthlyAE}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '') {
                            setPersonalMonthlyAE(0);
                          } else {
                            const val = parseFloat(raw);
                            setPersonalMonthlyAE(isNaN(val) || val < 0 ? 0 : val);
                          }
                        }}
                        className="w-full px-3 py-2 text-base sm:text-sm rounded-xl bg-slate-900/90 text-text-primary border border-white/10 focus:border-accent-emerald focus:outline-none font-mono"
                        placeholder="0"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-text-muted font-mono pointer-events-none">
                        Е
                      </span>
                    </div>
                  </div>

                  {/* Team Monthly AE (Summed from children) */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs">
                      <label className="font-semibold text-text-primary">
                        Командний обіг (Е):
                      </label>
                      <span className="font-mono text-[11px] text-cyan-400 font-semibold">
                        {childrenMonthly} Е
                      </span>
                    </div>
                    <p className="text-[10px] text-text-muted leading-tight">
                      {selectedNode.children.length > 0
                        ? `Сума з ${selectedNode.children.length} прямих гілок команди`
                        : 'У консультанта немає підпорядкованих гілок'}
                    </p>
                    <div className="w-full px-3 py-2 text-base sm:text-sm rounded-xl bg-slate-900/50 text-cyan-300 border border-white/5 font-mono flex items-center justify-between">
                      <span className="font-bold">{childrenMonthly} Е</span>
                      <span className="text-[10px] text-text-muted font-sans">
                        (сумується з гілок)
                      </span>
                    </div>
                  </div>
                </div>

                {/* Monthly Total Breakdown Pill */}
                <div className="px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/5 flex items-center justify-between text-[11px]">
                  <span className="text-text-secondary">Структура місячного обігу:</span>
                  <span className="font-mono text-text-primary font-medium">
                    Власні: <span className="text-emerald-400">{isActive ? personalMonthlyAE : 0} Е</span> + Команда: <span className="text-cyan-400">{childrenMonthly} Е</span>
                  </span>
                </div>
              </div>

              {/* 2. Cumulative Units Section (Personal + Team) */}
              <div className="p-3.5 rounded-xl bg-slate-900/60 border border-white/10 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-text-primary uppercase tracking-wide">
                    Накопичені одиниці (Е)
                  </span>
                  <span className="font-mono text-xs text-purple-300 font-bold">
                    Всього: {personalCumulativeAE + teamCumulativeAE} Е
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Personal Cumulative AE */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs">
                      <label htmlFor="drawer-personal-cumulative-ae" className="font-semibold text-text-primary">
                        Особисті накопичені (Е):
                      </label>
                      <span className="font-mono text-[11px] text-emerald-400 font-semibold">
                        {personalCumulativeAE} Е
                      </span>
                    </div>
                    <p className="text-[10px] text-text-muted leading-tight">
                      Особисті бали (вимога Р3+: від 300 Е)
                    </p>
                    <div className="relative">
                      <input
                        id="drawer-personal-cumulative-ae"
                        type="number"
                        min={0}
                        step={1}
                        value={personalCumulativeAE === 0 ? '' : personalCumulativeAE}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '') {
                            setPersonalCumulativeAE(0);
                          } else {
                            const val = parseFloat(raw);
                            setPersonalCumulativeAE(isNaN(val) || val < 0 ? 0 : val);
                          }
                        }}
                        className="w-full px-3 py-2 text-base sm:text-sm rounded-xl bg-slate-900/90 text-text-primary border border-white/10 focus:border-accent-emerald focus:outline-none font-mono"
                        placeholder="0"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-text-muted font-mono pointer-events-none">
                        Е
                      </span>
                    </div>
                  </div>

                  {/* Team Cumulative AE */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs">
                      <label htmlFor="drawer-team-cumulative-ae" className="font-semibold text-text-primary">
                        Командні накопичені (Е):
                      </label>
                      <span className="font-mono text-[11px] text-purple-300 font-semibold">
                        {teamCumulativeAE} Е
                      </span>
                    </div>
                    <p className="text-[10px] text-text-muted leading-tight">
                      Накопичені бали команди
                    </p>
                    <div className="relative">
                      <input
                        id="drawer-team-cumulative-ae"
                        type="number"
                        min={0}
                        step={1}
                        value={teamCumulativeAE === 0 ? '' : teamCumulativeAE}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '') {
                            setTeamCumulativeAE(0);
                          } else {
                            const val = parseFloat(raw);
                            setTeamCumulativeAE(isNaN(val) || val < 0 ? 0 : val);
                          }
                        }}
                        className="w-full px-3 py-2 text-base sm:text-sm rounded-xl bg-slate-900/90 text-text-primary border border-white/10 focus:border-purple-400 focus:outline-none font-mono"
                        placeholder="0"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-text-muted font-mono pointer-events-none">
                        Е
                      </span>
                    </div>
                  </div>
                </div>

                {/* Cumulative Total Breakdown Pill */}
                <div className="px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/5 flex items-center justify-between text-[11px]">
                  <span className="text-text-secondary">Структура накопичених балів:</span>
                  <span className="font-mono text-text-primary font-medium">
                    Власні: <span className="text-emerald-400">{personalCumulativeAE} Е</span> + Команда: <span className="text-purple-300">{teamCumulativeAE} Е</span>
                  </span>
                </div>
              </div>
            </div>

            {/* Monthly Meetings */}
            <Slider
              label="Кількість проведених зустрічей/міс"
              value={monthlyMeetings}
              min={0}
              max={40}
              step={1}
              unit="зустрічей"
              onChange={setMonthlyMeetings}
            />

            {/* Conversion: Meetings per contract (3 to 20) */}
            <Slider
              label="Бесід на 1 контракт"
              description="Кількість бесід для укладання 1 контракту в середньому"
              value={conversionRate > 0 ? Math.min(20, Math.max(3, Math.round(1 / conversionRate))) : 10}
              min={3}
              max={20}
              step={1}
              valueFormatter={(val) => `${val} бесід (${(100 / val).toFixed(1)}%)`}
              onChange={(val) => setConversionRate(1 / val)}
            />

            {/* Recruitment Rate */}
            <Slider
              label="Швидкість рекрутингу"
              value={recruitmentRate}
              min={0}
              max={4.0}
              step={0.1}
              valueFormatter={(val) => `${val.toFixed(1)} чол./міс`}
              onChange={setRecruitmentRate}
            />

            {/* Add Direct Subordinate Button */}
            {onAddChild && (
              <div className="pt-2">
                <Button
                  variant="outline"
                  fullWidth
                  leftIcon={<UserPlus className="w-4 h-4 text-accent-neon" />}
                  onClick={() => {
                    onAddChild(selectedNode.id);
                  }}
                >
                  Додати пряму лінію до цього консультанта (+)
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </Drawer>
  );
};

export const NodeEditorDrawer: React.FC<NodeEditorDrawerProps> = (props) => {
  if (!props.selectedNode) return null;
  return <NodeEditorDrawerContent {...props} selectedNode={props.selectedNode} />;
};

